/**
 * Tenzen navigation: desktop two pills, m-web app bar + screens.
 *
 * Deliberately thin. Everything that already exists in the theme is reused:
 *   - the desktop hamburger calls `showModal()` on the existing
 *     `<dialog data-menu-panel>` side drawer (sections/header.liquid:289).
 *   - cart and search buttons carry `data-open-cart` / `data-open-search`,
 *     both delegated on `document` by the theme's own scripts.
 *   - the desktop country panel is cloned out of the live header DOM.
 *
 * What this file owns on m-web is the view stack. There are four views:
 * the page itself plus the three screens (browse, global, account), ordered
 * left to right exactly as the tabs are. Moving right pushes the incoming
 * view in from the right while the outgoing one parallaxes left; moving left
 * reverses it, so tapping Home slides the page back in from the left. That
 * is the whole reason the page's own blocks are transformed too: Aditya
 * asked for Home to travel, not just sit there while an overlay appears.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';

  /* Tab order, left to right. Direction of travel is derived from it.

     `global` left this list on 2026-09-14 when Aditya folded the Global tab
     into Account; the region picker is a card rail inside the Account
     screen now. The global PANEL is still rendered and still opens - from
     the desktop deck's `data-tz-pane="global"` button, which runs through
     `wirePanes()`, not through this stack - so it must not be re-added here
     or `rank()` would start reporting a slide direction for a screen the
     m-web bar can no longer reach. */
  var ORDER = ['home', 'browse', 'account'];

  /* The layout's three top-level blocks (layout/theme.liquid:258-270).
     `shopify-section-group-*` is a CLASS on each section, not an id. */
  var PAGE_SEL = '#MainContent, .shopify-section-group-header-group, ' +
                 '.shopify-section-group-footer-group';

  /* Which tab a template implies when no screen is open. `search` maps to a
     rank so direction still works, but marks nothing: search is the FAB. */
  var BASE = {
    'index': { tab: 'home', mark: true },
    'collection': { tab: 'browse', mark: true },
    'list-collections': { tab: 'browse', mark: true },
    'search': { tab: 'browse', mark: false }
  };

  function cloneCountry(panel) {
    if (!panel || panel.childElementCount) return !!(panel && panel.childElementCount);
    var src = document.querySelector('#v-header-country .v-header__panel-clip');
    if (!src) return false;
    var copy = src.cloneNode(true);
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
    panel.appendChild(copy);
    return true;
  }

  /**
   * The sliding selection. One element travels to whichever tab is current,
   * rather than each tab cross-fading its own background.
   */
  function makeSelection() {
    var bar = document.querySelector('.tz-app__bar');
    var sel = document.querySelector('[data-tz-sel]');
    if (!bar || !sel) return function () {};

    return function move() {
      var active = bar.querySelector('.tz-tab[aria-current="page"]');
      if (!active) { sel.removeAttribute('data-tz-ready'); return; }
      var b = bar.getBoundingClientRect();
      var r = active.getBoundingClientRect();
      /* No rounding. Four flex tabs in a 289px bar are 70.25px each, so
         rounding both the offset and the width can drift the last tab's
         right edge a pixel away from the first tab's left edge. Sub-pixel
         values compose exactly and the compositor handles the rest. */
      sel.style.width = r.width + 'px';
      sel.style.transform = 'translateX(' + (r.left - b.left) + 'px)';
      sel.setAttribute('data-tz-ready', '');
    };
  }

  /**
   * The view stack. `view` is 'page' or a screen key; every move is a push
   * or a pop decided by the tabs' own left-to-right order.
   */
  function wireScreens(moveSel) {
    var panels = {};
    document.querySelectorAll('[data-tz-screen-panel]').forEach(function (el) {
      panels[el.getAttribute('data-tz-screen-panel')] = el;
    });

    var pageParts = Array.prototype.slice.call(document.querySelectorAll(PAGE_SEL));
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var DUR = reduce ? 0 : 300;
    var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    /* LINEAR, not a stack. Aditya: every tab lives on the same plane, so
       switching slides the current view fully OUT of frame while the next
       one comes fully in - one filmstrip, no depth.

       -100 is the whole change. At the old -24 (an iOS-style parallax) the
       outgoing view only crept sideways and the incoming slid over the top
       of it, which is exactly the overlap being described. The panels
       already share `z-index: 1000`, so nothing stacks once the two halves
       of the move travel the same distance. */
    var OUT = -100;

    var cls = document.body.className || '';
    var found = cls.match(/template-([a-z0-9_-]+)/);
    var entry = BASE[found ? found[1] : 'index'] || BASE.index;
    var baseTab = entry.tab;
    var baseMark = entry.mark ? entry.tab : null;
    if (/\/pages\/account-hub/.test(window.location.pathname)) {
      baseTab = 'account';
      baseMark = 'account';
    }

    var view = 'page';
    var busy = false;

    function partsFor(key) { return key === 'page' ? pageParts : [panels[key]]; }

    function place(key, pct, animate) {
      partsFor(key).forEach(function (el) {
        if (!el) return;
        el.style.transition = (animate && DUR) ? 'transform ' + DUR + 'ms ' + EASE : 'none';
        el.style.transform = 'translateX(' + pct + '%)';
      });
    }

    /* Drop the inline transform entirely once a move has settled. A
       transformed ancestor becomes the containing block for any
       `position: fixed` descendant, so the page must not stay transformed a
       moment longer than the animation needs it. Safe to do while a screen
       is open: the screen is opaque and covers the viewport. */
    function clearTransform(key) {
      partsFor(key).forEach(function (el) {
        if (!el) return;
        el.style.transition = '';
        el.style.transform = '';
      });
    }

    /* A key outside ORDER (the Global screen, opened from the desktop deck)
       would come back -1 and read as "leftmost", sliding the wrong way.
       Park it past the end instead so it always pushes forward. */
    function rank(key) {
      var i = ORDER.indexOf(key === 'page' ? baseTab : key);
      return i === -1 ? ORDER.length : i;
    }

    function syncTabs() {
      var active = view === 'page' ? baseMark : view;
      document.querySelectorAll('[data-tz-tab]').forEach(function (t) {
        if (active && t.getAttribute('data-tz-tab') === active) {
          t.setAttribute('aria-current', 'page');
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (moveSel) moveSel();
    }

    function go(next) {
      if (busy || next === view) return;
      if (next !== 'page' && !panels[next]) return;

      var from = view;
      var fwd = rank(next) >= rank(from);
      busy = true;

      if (next !== 'page') panels[next].hidden = false;
      document.documentElement.classList.toggle('tz-screen-open', next !== 'page');

      /* Seed the incoming view on the correct side, committed before the
         animated frame so the browser has two distinct values to tween. */
      place(next, fwd ? 100 : OUT, false);
      partsFor(next).forEach(function (el) { if (el) void el.offsetWidth; });

      place(next, 0, true);
      place(from, fwd ? OUT : 100, true);

      view = next;
      syncTabs();

      window.setTimeout(function () {
        if (from === 'page') {
          clearTransform('page');
        } else {
          place(from, 100, false);
          panels[from].hidden = true;
        }
        if (next === 'page') clearTransform('page'); else place(next, 0, false);
        busy = false;
      }, DUR + 30);
    }

    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-tz-screen]');
      if (!tab) return;
      var key = tab.getAttribute('data-tz-screen');

      /* Home is a real <a href="/">. While a screen is open it means "back",
         so intercept; otherwise let it navigate, unless we are already on
         the home page and there is nothing to do. */
      if (key === 'home') {
        if (view !== 'page') { e.preventDefault(); go('page'); return; }
        if (baseTab === 'home') e.preventDefault();
        return;
      }

      e.preventDefault();
      go(key);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && view !== 'page') go('page');
    });

    syncTabs();
    window.__tenzenNav = { go: go, current: function () { return view; } };
  }

  /**
   * Top bar: back, cart, search. Hides on scroll down and comes straight
   * back on the first scroll up, with no velocity threshold, because Aditya
   * asked for a slight scroll up to reveal it. Near the top of the page it
   * is always shown, so a short page can never strand it off-screen.
   */
  function wireTopbar() {
    var bar = document.querySelector('[data-tz-topbar]');
    if (!bar) return;
    bar.hidden = false;

    /* Asymmetric on purpose. Aditya asked for "a slight scroll up to reveal
       it", so coming back stays cheap at 6px - but 6px in the other
       direction meant the smallest possible flick dismissed the chrome, and
       on the collection page that dismissal moves the layout by more than
       100px (see SETTLE below). Dismissing now takes a deliberate 24px. */
    var THRESH_HIDE = 24;
    var THRESH_SHOW = 6;
    var TOP_ZONE = 48;  // always visible this close to the top

    /* ── The stutter lock ──────────────────────────────────────────────
       Aditya, 2026-09-14, with a screen recording of a collection page: "if
       I swipe down very minimally, like a very small flick, the page gets
       glitched because it cannot decide what state of the header should
       render."

       It is a feedback loop, and the collection page is where it bites
       because that page has the most layout riding on this one class.
       `html.tz-chrome-hidden` moves `.v-col__rail`'s sticky `top` from
       `64px + safe-area + --tz-bar-h` (about 167px) to just the safe-area
       inset (about 59px) - and animates it. A stuck rail sliding 108px
       makes the browser's scroll anchoring correct the scroll position to
       keep content still. That correction arrives as a `scroll` event.

       The old code then read it as a real gesture: `last` was only updated
       when `|dy|` cleared the threshold, so after a toggle it still held a
       pre-toggle position and the induced scroll came back as a large delta
       in the OPPOSITE direction. Which toggled the class back. Which moved
       the rail again. At a 6px threshold against a 100px-plus shift, once
       it starts it cannot stop - which is exactly the flip-flopping in the
       recording.

       So: for as long as the transitions this toggle started are still
       running, keep re-baselining `last` to the live position and change
       nothing. Any movement the toggle CAUSED is absorbed into the
       baseline instead of being mistaken for the user. 300ms covers the
       260ms `top` transition on the rail with a frame or two to spare. */
    var SETTLE = 300;
    var lockUntil = 0;
    var last = window.scrollY || window.pageYOffset || 0;
    var ticking = false;
    var hidden = false;

    function now() {
      return (window.performance && performance.now) ? performance.now() : Date.now();
    }

    /* One signal for all floating chrome. The collection page's own sticky
       title bar keys off `tz-chrome-hidden` too, so the circles and the
       compacted header hide and return as one thing instead of one of them
       staying pinned while the other slides away. */
    function setHidden(on) {
      if (on === hidden) return;
      hidden = on;
      if (on) bar.setAttribute('data-tz-hidden', '');
      else bar.removeAttribute('data-tz-hidden');
      document.documentElement.classList.toggle('tz-chrome-hidden', on);
      lockUntil = now() + SETTLE;
    }

    function update() {
      ticking = false;
      var y = Math.max(0, window.scrollY || window.pageYOffset || 0);

      if (now() < lockUntil) { last = y; return; }

      var dy = y - last;
      if (y < TOP_ZONE) {
        setHidden(false);
        last = y;
      } else if (dy > THRESH_HIDE) {
        setHidden(true);
        last = y;
      } else if (dy < -THRESH_SHOW) {
        setHidden(false);
        last = y;
      }
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    /* Back means back. Only fall through to the home link when there is no
       history to pop, which is the case for a direct hit or a new tab. */
    bar.addEventListener('click', function (e) {
      if (!e.target.closest('[data-tz-back]')) return;
      if (window.history.length > 1) {
        e.preventDefault();
        window.history.back();
      }
    });
  }

  /**
   * Bottom deck: full height with labels at rest, shorter, narrower and
   * icons-only once the surface underneath is scrolled down, back to full on
   * the first scroll up.
   *
   * Aditya, 2026-09-14, against an Instagram reference: "when I scroll down,
   * the navigation should go from 60px height to 40px compact version where
   * there is no label, just the icons and it should be center aligned", then
   * "make it compact on all the tabs. On the Browse tab and on the Accounts
   * tab as well".
   *
   * WHICH SCROLLER. The home page scrolls the document; the Browse and
   * Account screens do not. They are `position: fixed; inset: 0` and the
   * thing that actually scrolls is their own `.tz-screen__body`. A window
   * listener hears nothing from them, which is why the deck stayed tall on
   * those two tabs. So the handler resolves the ACTIVE scroller on every
   * pass - the open panel's body, or the window - and reads that.
   *
   * `capture: true` is doing real work here: a `scroll` event fired at an
   * element does not bubble, so a listener on `document` only ever sees it
   * on the way DOWN. One listener then covers every panel, including any
   * added later, with no per-panel binding to keep in step.
   *
   * Each scroller keeps its OWN last position in a WeakMap. Sharing one
   * would mean that switching from a page scrolled to 2000px to a panel
   * sitting at 0 produced a -2000px delta and snapped the deck open on a
   * scroll nobody performed.
   *
   * Same thresholds and the same rAF-coalesced pass as `wireTopbar`, but a
   * SEPARATE listener: `wireTopbar` returns early when a template renders no
   * `[data-tz-topbar]`, and the deck is on every m-web page.
   */
  function wireNavCompact(moveSel) {
    var app = document.querySelector('[data-tz-app]');
    if (!app) return;
    var sel = app.querySelector('[data-tz-sel]');
    var bar = app.querySelector('.tz-app__bar');

    /* Mirrors `wireTopbar`: cheap to bring back, deliberate to dismiss.
       This listener runs on the same scroll stream as the top bar on the
       same pages, so leaving it at a flat 6px would have kept the deck
       flip-flopping even after the header was steady. */
    var THRESH_HIDE = 24;
    var THRESH_SHOW = 6;
    var TOP_ZONE = 48;  // always full size this close to the top
    var DUR = 340;      // must outlast the CSS transition (320ms)
    /* Same settle lock as the top bar, for the same reason: the collapse
       animates chrome that other sticky elements are positioned against,
       and any scroll correction that causes must not read as a gesture. */
    var SETTLE = 340;
    var lockUntil = 0;
    /* 3 tabs x 48 + the bar's own 4px padding either side. Paired with
       `html.tz-nav-compact .tz-tab` in the stylesheet; the tabs are
       `flex: 1 1 0` and divide whatever this is. */
    var COMPACT_W = 152;
    var ticking = false;
    var compact = false;
    var winLast = Math.max(0, window.scrollY || window.pageYOffset || 0);
    var lastFor = new WeakMap();
    var restW = 0;      // the bar's width at full size, kept current below
    var unpin = null;

    /* Record the resting width whenever the bar is at full size and not
       pinned. The bag button appears and disappears with the cart, which
       moves ~54px in and out of the row, so a value captured once at load
       goes stale the first time someone adds something. */
    if (bar && window.ResizeObserver) {
      new ResizeObserver(function () {
        if (!compact && !bar.style.flexBasis) restW = bar.getBoundingClientRect().width;
      }).observe(bar);
    }

    function activeScroller() {
      var open = document.querySelector('[data-tz-screen-panel]:not([hidden])');
      var body = open && open.querySelector('.tz-screen__body');
      return body || null;   // null means the window
    }
    function posOf(sc) {
      return sc ? sc.scrollTop : Math.max(0, window.scrollY || window.pageYOffset || 0);
    }

    /* The sliding selection is measured off the tab's box, and that box is
       changing size for the whole duration of the collapse. Reading it once
       at the end - which is what `transitionend` gave us - left the pill at
       its old size for the entire animation and then jumped it, which was
       most of what read as clunky.
       Driven per frame instead, with its own easing switched off so the CSS
       transition is not trying to smooth a value that is already smooth. */
    function trackSelection() {
      if (!moveSel) return;
      if (sel) sel.style.transition = 'none';
      var t0 = (window.performance && performance.now()) ? performance.now() : Date.now();
      (function step() {
        moveSel();
        var now = (window.performance && performance.now()) ? performance.now() : Date.now();
        if (now - t0 < DUR) requestAnimationFrame(step);
        else if (sel) sel.style.transition = '';
      })();
    }

    function set(on) {
      if (on === compact) return;
      compact = on;
      lockUntil = nowMs() + SETTLE;

      /* Pin the bar's width for the duration of the collapse.

         The bar is `flex: 1 1 0`, so the moment the two FABs begin shrinking
         it inherits the ~28px they give back and GROWS - measured 222 -> 230
         - before the animating `max-width` has come down far enough to
         clamp it. An eighth of a second of the bar getting wider on its way
         to getting narrower is small, but it is the exact thing that reads
         as clunky.
         Freezing the basis at the width it already has, with no grow, means
         there is nothing for it to absorb: the bar holds still until
         max-width passes under it, then narrows the whole way. Cleared on
         the way back out, where `max-width` animating upward is already
         doing the constraining. */
      /* ── The width, animated between two MEASURED endpoints ──────────
         Both directions used to be wrong in the same way, with opposite
         signs. The bar is `flex: 1 1 0`, so it takes whatever the row has
         spare - and the two FABs change size at the same moment it does.
         Collapsing, it grabbed the ~28px they gave back and grew 222 -> 230
         before narrowing. Expanding, it filled a row whose FABs were still
         small and shot to 242 before creeping back to 222. Either way the
         bar moved the wrong way first, which is what read as clunky.

         So the bar is taken out of the flex negotiation for the duration:
         grow 0, and an explicit basis animated from where it is to where it
         is going. The FABs can resize beside it without the bar reacting.

         Written in three steps because the resting computed `flex-basis` is
         `0px` while the bar is visibly 222 wide - writing the start value
         straight in would animate 0 -> 222 and swipe the bar out of
         existence on the way. Transitions off, plant the start, flush the
         layout, transitions back, then the end value. */
      if (bar) {
        clearTimeout(unpin);
        if (!restW) restW = bar.getBoundingClientRect().width;
        var from = on ? restW : COMPACT_W;
        var to = on ? COMPACT_W : restW;

        bar.style.transitionProperty = 'none';
        bar.style.flexGrow = '0';
        bar.style.flexBasis = from + 'px';
        void bar.offsetWidth;              // flush: makes `from` the start
        bar.style.transitionProperty = '';

        document.documentElement.classList.toggle('tz-nav-compact', on);
        bar.style.flexBasis = to + 'px';

        if (!on) {
          /* Hand the bar back to normal flex once it has arrived, so a
             later layout change - a rotation, the bag appearing - is not
             fighting a stale inline width. */
          unpin = setTimeout(function () {
            bar.style.flexBasis = '';
            bar.style.flexGrow = '';
          }, DUR);
        }
      } else {
        document.documentElement.classList.toggle('tz-nav-compact', on);
      }

      trackSelection();
    }

    function nowMs() {
      return (window.performance && performance.now) ? performance.now() : Date.now();
    }

    function update() {
      ticking = false;
      var sc = activeScroller();
      var y = posOf(sc);

      function rebase() { if (sc) lastFor.set(sc, y); else winLast = y; }

      if (nowMs() < lockUntil) { rebase(); return; }

      var last = sc ? (lastFor.has(sc) ? lastFor.get(sc) : 0) : winLast;
      var dy = y - last;

      if (y < TOP_ZONE) { set(false); rebase(); }
      else if (dy > THRESH_HIDE) { set(true); rebase(); }
      else if (dy < -THRESH_SHOW) { set(false); rebase(); }
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    document.addEventListener('scroll', schedule, { passive: true, capture: true });

    /* Switching tabs swaps the scroller underneath the deck. Re-read the new
       one so a panel opening at the top restores full height, rather than
       the deck keeping whatever state the previous surface left it in.
       Two frames: the panel is `hidden` until the tab is tapped, and a hidden
       element has no scroll position to read. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-tz-screen], [data-tz-pane-close]')) return;
      requestAnimationFrame(function () { requestAnimationFrame(update); });
    });
  }

  function wireRegionRail() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-tz-screen="account"], [data-tz-pane="account"]')) return;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var rail = document.querySelector('[data-tz-regrail]');
          var cur = rail && rail.querySelector('[data-tz-regcurrent]');
          if (!cur) return;
          /* Left-align it, not `scrollIntoView`, which scrolls every
             scrollable ancestor including the panel body and would jump the
             screen down to the rail.

             Minus the rail's own left padding, so the parked card lands on
             the same 16px line as the eyebrow above it. Without it the card
             sits flush to the screen edge - inside the padding - and reads
             as a rail that has been nudged half out of the layout. */
          var pad = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
          rail.scrollLeft = Math.max(0, cur.offsetLeft - rail.offsetLeft - pad);
        });
      });
    });
  }

  function wireDrawer() {
    // Capture phase, so this runs before the theme's own document-level
    // handlers and there is no chance of both firing.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tz-open-drawer]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var dlg = document.querySelector('[data-menu-panel]');
      if (!dlg || typeof dlg.showModal !== 'function') return;
      if (dlg.open) {
        dlg.close();
        document.documentElement.classList.remove('tz-drawer-page');
      } else {
        document.documentElement.classList.add('tz-drawer-page');
        dlg.showModal();
        dlg.addEventListener('close', function once() {
          document.documentElement.classList.remove('tz-drawer-page');
          dlg.removeEventListener('close', once);
        });
      }
    }, true);
  }

  function closePanels(except) {
    document.querySelectorAll('[data-tz-toggle]').forEach(function (b) {
      if (b.getAttribute('data-tz-toggle') === except) return;
      b.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('[data-tz-panel]').forEach(function (p) {
      if (p.getAttribute('data-tz-panel') === except) return;
      p.hidden = true;
    });
  }

  /** Desktop only: the country panel hanging off the right-hand pill. */
  function wirePanels() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tz-toggle]');
      if (btn) {
        e.preventDefault();
        var key = btn.getAttribute('data-tz-toggle');
        var open = btn.getAttribute('aria-expanded') === 'true';
        closePanels(open ? null : key);
        document.querySelectorAll('[data-tz-toggle="' + key + '"]').forEach(function (b) {
          b.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
        document.querySelectorAll('[data-tz-panel="' + key + '"]').forEach(function (p) {
          p.hidden = open;
        });
        return;
      }
      if (!e.target.closest('[data-tz-nav]') && !e.target.closest('[data-tz-panel]')) {
        closePanels(null);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closePanels(null);
    });
  }

  /**
   * Every bag button on the site, kept in step with the actual bag.
   *
   * Previously this watched `[data-header-cart-count]` with a
   * MutationObserver. That element lives inside the retired header deck,
   * which is now `display: none`, and it only ever reflected whichever
   * script happened to write to it. `window.VolatileCart`
   * (assets/cart-state-cartfix-8f4211b.js) is the real source of truth: it
   * hydrates from /cart.js on load, and `subscribe()` fires on every add,
   * every quantity change, every remove, including the optimistic updates
   * the drawer makes before the network round-trip.
   *
   * `subscribe` also fires once immediately with the current state, so
   * there is no separate first-render path.
   */
  function wireBagButtons() {
    var buttons = document.querySelectorAll('[data-tz-bag]');
    if (!buttons.length) return;

    var apply = function (n) {
      var has = n > 0;
      buttons.forEach(function (b) { b.hidden = !has; });
      document.querySelectorAll('[data-tz-count]').forEach(function (el) {
        el.textContent = String(n);
        el.hidden = !has;
      });
    };

    var bind = function () {
      if (!window.VolatileCart || typeof window.VolatileCart.subscribe !== 'function') return false;
      window.VolatileCart.subscribe(function (state) {
        apply(state && typeof state.item_count === 'number' ? state.item_count : 0);
      });
      return true;
    };

    /* ── Never trust the page's own idea of the bag ────────────────────
       The bag button's initial `hidden` comes from `cart.item_count` baked
       into the HTML, and that HTML is cached: add from a PDP, tap back, and
       the category page is restored from the browser's back/forward cache
       with the OLD count and NO `DOMContentLoaded` - so nothing re-ran and
       the bag stayed hidden until a manual reload. That is the bug.

       `pageshow` fires on a normal load AND on a bfcache restore, which is
       the only event that covers both. `visibilitychange` covers the other
       route in: adding to the bag in one tab, or leaving and coming back.
       Both just ask the cart what is actually in it. */
    var recheck = function () {
      if (window.VolatileCart && typeof window.VolatileCart.refresh === 'function') {
        window.VolatileCart.refresh();
        return;
      }
      if (typeof fetch !== 'function') return;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { apply(c && typeof c.item_count === 'number' ? c.item_count : 0); })
        .catch(function () {});
    };

    window.addEventListener('pageshow', recheck);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) recheck();
    });

    if (bind()) { recheck(); return; }
    // Script order is not guaranteed, so wait for its ready event.
    window.addEventListener('volatile:cart:ready', bind, { once: true });

    /* Last resort only: if VolatileCart never loads, fall back to the
       count the header still renders server-side, so the buttons are at
       least correct for the current page rather than stuck on. */
    var source = document.querySelector('[data-header-cart-count]');
    if (!source) return;
    var sync = function () {
      if (window.VolatileCart) return;
      var n = parseInt((source.textContent || '0').trim(), 10);
      apply(isNaN(n) ? 0 : n);
    };
    sync();
    new MutationObserver(sync).observe(source, {
      childList: true, characterData: true, subtree: true, attributes: true
    });
  }

  /**
   * Collection description, clamped to three lines with a clickable "..."
   * that expands it.
   *
   * Marked only when the text ACTUALLY overflows three lines, measured
   * against the element's own line-height, so a two-line description never
   * gets a "more" control that does nothing. Re-measured on resize while it
   * is still collapsed, because a rotation can change the line count.
   */
  /**
   * Collection description: three lines, with a two-way toggle.
   *
   * Marked only when the text ACTUALLY overflows three lines, measured
   * against the element's own line-height, so a short description never
   * gets a control that does nothing. Re-measured on resize while it is
   * still collapsed, because a rotation changes the line count.
   */
  function wireClamp() {
    var desc = document.querySelector('.v-col__desc');
    if (!desc || desc.hasAttribute('data-tz-clamp')) return;

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'tz-clamp-more';

    var setState = function (open) {
      desc.setAttribute('data-tz-clamp', open ? 'open' : 'closed');
      toggle.textContent = open ? 'Less' : 'More';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Always the last child, so it reads as the end of the copy whether
      // the copy is three lines or thirty.
      desc.appendChild(toggle);
    };

    var overflows = function () {
      var cs = window.getComputedStyle(desc);
      var lh = parseFloat(cs.lineHeight);
      if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.4;
      // 2 lines plus a pixel of tolerance for sub-pixel line boxes. Must
      // match `-webkit-line-clamp` in snippets/tenzen-topnav.liquid.
      return desc.scrollHeight > (lh * 2) + 1;
    };

    var measure = function () {
      if (desc.getAttribute('data-tz-clamp') === 'open') return;
      desc.setAttribute('data-tz-clamp', 'closed');
      if (toggle.isConnected) toggle.remove();
      if (overflows()) setState(false);
      else desc.removeAttribute('data-tz-clamp');
    };

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      setState(desc.getAttribute('data-tz-clamp') !== 'open');
    });
    // Tapping the clamped copy opens it. It does NOT close it again - that
    // would make every tap inside a long paragraph collapse it by accident.
    desc.addEventListener('click', function (e) {
      if (e.target === toggle) return;
      if (desc.getAttribute('data-tz-clamp') === 'closed') setState(true);
    });

    measure();
    window.addEventListener('resize', measure, { passive: true });
  }

  /**
   * Kills the focus ring every sheet and overlay came up wearing.
   *
   * `showModal()` moves focus to the dialog's first focusable descendant,
   * and browsers treat that programmatic move as keyboard-initiated - so
   * opening the bag, the menu or the filter sheet with a TAP still drew a
   * ring around whatever came first, usually the close button or the
   * heading. Redirecting focus to the dialog itself keeps the focus INSIDE
   * the dialog (screen readers still announce it, ESC and tab still work)
   * while giving the ring nothing to sit on, since a `tabindex="-1"` dialog
   * is not keyboard-reachable. The matching `outline: none` lives in
   * `snippets/tenzen-topnav.liquid`.
   *
   * An explicit `autofocus` inside is left alone - that is a deliberate
   * choice by whoever wrote the sheet, e.g. a search field that should take
   * the keyboard immediately.
   */
  function wireDialogFocus() {
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var d = recs[i].target;
        // `open` is also a <details> attribute, hence the tag check.
        if (!d || d.tagName !== 'DIALOG' || !d.hasAttribute('open')) continue;
        if (d.querySelector('[autofocus]')) continue;
        d.setAttribute('tabindex', '-1');
        try { d.focus({ preventScroll: true }); } catch (err) { d.focus(); }
      }
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true
    });
  }

  /**
   * The share button in the top-right pill.
   *
   * `navigator.share` opens the real OS sheet, which is the whole point on
   * the surface this button lives on - the top bar is mobile-only, so that
   * path is the normal one, not the fallback. Where it is missing the link
   * goes to the clipboard and the button says so; the wording comes from
   * the `data-tz-copied-label` attribute so it stays in the locale files
   * rather than being hard-coded here.
   *
   * A dismissed share sheet rejects, which is not an error - hence the
   * empty catch rather than a log.
   */
  function wireShare() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tz-share]');
      if (!btn) return;
      e.preventDefault();

      var payload = { title: document.title, url: window.location.href };
      if (navigator.share) {
        try { navigator.share(payload).catch(function () {}); } catch (err) {}
        return;
      }
      if (!navigator.clipboard || !navigator.clipboard.writeText) return;
      navigator.clipboard.writeText(window.location.href).then(function () {
        btn.setAttribute('data-tz-copied', '');
        window.setTimeout(function () { btn.removeAttribute('data-tz-copied'); }, 1600);
      }).catch(function () {});
    });
  }

  /**
   * Empties the bag when the visitor changes region.
   *
   * A bag priced for one market is not a bag priced for another, and this
   * store resolves every market to INR anyway - so carrying items across a
   * switch means carrying figures the customer was shown under different
   * assumptions. Aditya's call: flush it.
   *
   * Keyed off `Shopify.country` compared against the last one seen in
   * localStorage, rather than off the picker's submit. The picker POSTs and
   * redirects, so a handler bound to it would be torn down mid-flight - and
   * it is not the only way the market can change (Shopify's own geo
   * redirect and the header's picker both do it too). Checking on arrival
   * catches every route in.
   *
   * `/cart/clear.js` is Shopify's own endpoint and leaves the cart token
   * intact, so nothing about the Magic Checkout path is disturbed. The UI
   * comes back through `VolatileCart.refresh()`, which re-reads `/cart.js`
   * and broadcasts - no reload.
   */
  function wireMarketReset() {
    var KEY = 'tz-market';
    var INTENT = 'tz-market-switch';

    /* The picker is the only thing allowed to empty a bag.

       This used to fire on any mismatch between the market the page
       rendered for and the last one seen in storage, which looked
       equivalent and is not: `localization.country.iso_code` is not
       stable per visitor. The same phone can be served IN on one request
       and the store's fallback market on the next, and every flip wiped a
       bag the customer had just filled - the "item disappears a second
       after I open the bag" report. A rendered value is a hint; only a
       tap on the region row is a decision.

       Recorded on the way out, in sessionStorage so it lives exactly as
       long as the submit-and-redirect it belongs to, and read once on
       arrival. */
    document.addEventListener('click', function (e) {
      var row = e.target.closest('button[name="country_code"]');
      if (!row) return;
      try {
        window.sessionStorage.setItem(INTENT, String(row.value || '').toUpperCase());
      } catch (err) {}
    });

    /* `window.TZ_MARKET` is printed by `snippets/tenzen-topnav.liquid` from
       `localization.country.iso_code`, which is the market the page was
       actually RENDERED for. `Shopify.country` is the fallback: it is the
       same value in normal conditions, but it is written by a separate
       Shopify script and is not guaranteed to be present, or updated, on
       every template. */
    var now = String(window.TZ_MARKET || (window.Shopify && window.Shopify.country) || '')
      .toUpperCase();
    if (!now) return;

    var last = null;
    /* Storage throws rather than returning null when it is blocked, and a
       failure here must never cost someone their bag. */
    try { last = window.localStorage.getItem(KEY); } catch (e) { return; }
    try { window.localStorage.setItem(KEY, now); } catch (e) {}

    /* Claim the intent whatever happens next, so a switch that failed to
       take cannot sit in storage and empty a later bag. */
    var asked = null;
    try {
      asked = window.sessionStorage.getItem(INTENT);
      window.sessionStorage.removeItem(INTENT);
    } catch (e) { return; }

    /* Three conditions, all required: the visitor asked for a switch, the
       page came back in the market they asked for, and it is genuinely a
       different one from the market they were last served. */
    if (!asked || asked !== now) return;
    if (!last || last === now) return;

    /* An explicit `{}` body: Shopify's endpoint is happy without one, but
       some edges reject a POST that declares JSON and then sends nothing. */
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'same-origin'
    }).then(function () {
      if (window.VolatileCart && typeof window.VolatileCart.refresh === 'function') {
        window.VolatileCart.refresh();
      }
    }).catch(function () {});
  }

  /**
   * The hand-off to Shopify's Account Center.
   *
   * That page is on account.tenzen.in, so this is a cross-domain
   * navigation: a blank moment on a slow connection, with no clue where
   * the tap went. The overlay covers it and says so.
   *
   * It deliberately does NOT try to fade out when the destination has
   * loaded - by then this document is gone and nothing here is running.
   * What it does instead is clear itself on the way BACK, which is the
   * only moment this page runs again, so returning never lands on a white
   * screen. `pageshow` is the event for that: a back-navigation restores
   * from the bfcache without firing `DOMContentLoaded`.
   *
   * The link is a real <a href> to `routes.account_url`, so it still works
   * with JS off, opens in a new tab on a modifier-click, and is not
   * intercepted here - the navigation is the browser's, not ours.
   */
  function wireAccountCenter() {
    var veil = document.querySelector('[data-tz-leaving]');
    if (!veil) return;

    var hide = function () {
      veil.removeAttribute('data-tz-on');
      veil.hidden = true;
    };

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-tz-account-center]');
      if (!link) return;
      // Let a modifier-click open a tab without covering this page.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      veil.hidden = false;
      // One frame between `display` and `opacity` or the transition is skipped.
      requestAnimationFrame(function () { veil.setAttribute('data-tz-on', ''); });
    });

    window.addEventListener('pageshow', hide);
    window.addEventListener('pagehide', hide);
  }

  /**
   * Desktop: the same three screens, as side panes.
   *
   * Deliberately NOT `wireScreens`. That is a four-view push/pop stack that
   * transforms the page itself, because on m-web Aditya wanted Home to
   * travel rather than sit still under an overlay. On desktop these are
   * panes over a page that stays exactly where it is, so there is no stack,
   * no direction, no parallax and nothing to rank - just one panel open at a
   * time. Sharing the stack here would have meant sliding the whole desktop
   * layout sideways to show a menu.
   *
   * Geometry and side live entirely in CSS (`[data-tz-screen-panel]`); this
   * only owns which panel is open.
   */
  function wirePanes() {
    var panels = {};
    document.querySelectorAll('[data-tz-screen-panel]').forEach(function (el) {
      panels[el.getAttribute('data-tz-screen-panel')] = el;
    });

    var root = document.documentElement;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* Mirrors the 360ms transition on `.tz-screen` in the desktop block of
       snippets/tenzen-topnav.liquid. Change one, change the other. */
    var DUR = reduce ? 0 : 360;
    var openKey = null;
    var timer = 0;

    function close() {
      if (!openKey) return;
      var el = panels[openKey];
      openKey = null;
      root.classList.remove('tz-pane-open');
      if (!el) return;
      el.removeAttribute('data-tz-pane-open');
      /* Let the slide finish before pulling it out of the layout. Setting
         `hidden` straight away makes the pane vanish rather than leave. */
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { el.hidden = true; }, DUR + 30);
    }

    function open(key) {
      var el = panels[key];
      if (!el) return;
      if (openKey === key) { close(); return; }   /* same control toggles */

      if (openKey && panels[openKey]) {
        panels[openKey].removeAttribute('data-tz-pane-open');
        panels[openKey].hidden = true;
      }
      window.clearTimeout(timer);
      openKey = key;

      /* If the viewport was resized down to m-web and back, `wireScreens`
         may have left an inline transform on this panel. CSS owns position
         on desktop, so clear anything inline before opening. */
      el.style.transition = '';
      el.style.transform = '';

      el.hidden = false;
      /* Commit the closed transform first. Dropping `hidden` and setting the
         open attribute in the same frame gives the browser one computed
         value instead of two, and the pane appears rather than slides. */
      void el.offsetWidth;
      el.setAttribute('data-tz-pane-open', '');
      root.classList.add('tz-pane-open');
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tz-pane]');
      if (btn) { e.preventDefault(); open(btn.getAttribute('data-tz-pane')); return; }
      if (e.target.closest('[data-tz-pane-close]')) { e.preventDefault(); close(); return; }
      /* Backstop. The scrim covers everything below the pane, so in practice
         an outside click lands on it and is handled above; this catches the
         case where something sits over the scrim. Guarded so a click on the
         pane's own chrome never closes it. */
      if (openKey && !e.target.closest('[data-tz-screen-panel]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    window.__tenzenPanes = {
      open: open,
      close: close,
      current: function () { return openKey; }
    };
  }

  function start() {
    var desktop = window.matchMedia(DESKTOP).matches;

    wireBagButtons();
    wireClamp();
    wireDialogFocus();
    wireMarketReset();
    wireAccountCenter();
    /* A restored page never re-runs `start()`, and a market switch is
       exactly the kind of thing that happens either side of a back. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) wireMarketReset();
    });

    if (desktop) {
      document.querySelectorAll('[data-tz-panel="country"]').forEach(cloneCountry);
      document.documentElement.classList.add('tz-topnav-on');
      wirePanels();
      wirePanes();
    } else {
      document.documentElement.classList.add('tz-appnav-on');
      wireTopbar();
      wireShare();
      var move = makeSelection();
      wireScreens(move);
      wireNavCompact(move);
      wireRegionRail();
      // First paint: place the indicator without animating in from zero width.
      requestAnimationFrame(move);
      window.addEventListener('resize', move, { passive: true });

      /* The bar's width is not settled at first paint. The bag button is
         rendered `hidden` when Liquid believes the cart is empty and the
         cart script un-hides it a moment later, which takes ~46px out of the
         row and re-divides every tab. The indicator had already measured the
         WIDER bar by then and sat over-wide on the first tab until something
         else moved it.

         Watching the bar itself covers that and every other late resize -
         the webfont landing, a locale with longer labels, the compact
         collapse - without having to know which one happened. */
      if (window.ResizeObserver) {
        var bar = document.querySelector('.tz-app__bar');
        if (bar) new ResizeObserver(move).observe(bar);
      }
    }

    wireDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
